import { ApiType } from "@gymflow/common";
import { utcToZonedTime, zonedTimeToUtc } from "@gymflow/helpers";
import { CalendarAppointment } from "@gymflow/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Optional } from "utility-types";

import { calendarQueryKeys } from "./calendarQueryKeys";

export function useInfiniteQueryCalendarAppointments(
  {
    api,
    tz,
    filters,
  }: {
    api: ApiType;
    tz: string;
    filters: Optional<CalendarAppointmentFilter, "dateFrom" | "dateTo">;
  },
  opts: { enabled?: boolean } = { enabled: true },
) {
  return useInfiniteQuery({
    queryKey: calendarQueryKeys.appointments({ tz, filters }),
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const result = await api.eventApi.calendarAppointments({
        filters: createApiParams({
          filters: {
            ...(filters as CalendarAppointmentFilter),
            nextPageToken: pageParam,
          },
          tz,
        }),
      });
      return result.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    select: (data) => calendarAppointmentsSelectFn(data, tz),
    enabled:
      opts.enabled &&
      !!tz &&
      (!!filters?.["dateFrom"] || !!filters?.["dateTo"]),
    initialPageParam: undefined,
  });
}

const createApiParams = ({
  filters,
  tz,
}: {
  filters: CalendarAppointmentFilter;
  tz: string;
}) => {
  const parsed: { dateFrom?: string; dateTo?: string } = {};

  if (filters?.["dateFrom"]) {
    parsed["dateFrom"] = zonedTimeToUtc(filters["dateFrom"], tz);
  }

  if (filters?.["dateTo"]) {
    parsed["dateTo"] = zonedTimeToUtc(filters["dateTo"], tz);
  }

  return {
    ...filters,
    ...parsed,
  };
};

export function calendarAppointmentsSelectFn(
  data: {
    pages: CalendarAppointmentResult[];
    pageParams: unknown[];
  },
  tz: string,
) {
  return {
    ...data,
    pages: data.pages.map((page) =>
      page.content.map((appointment) => ({
        ...appointment,
        startDate: utcToZonedTime(appointment.startDate, tz),
        endDate: utcToZonedTime(appointment.endDate, tz),
      })),
    ),
  };
}

interface CalendarAppointmentFilter {
  dateFrom: string;
  dateTo: string;
  limit: number;
  nextPageToken?: string;
  appointmentHostId?: string[];
  appointableId?: number[];
  appointmentFacilityId?: number[];
  appointableCategoryIds?: number[];
}

interface CalendarAppointmentResult {
  content: CalendarAppointment[];
  nextPageToken?: string;
}
